import { Icon } from "@ugitgud/legos/ui/icon/Icon";
import { ReactNode } from "react";
import { getYirSVG } from "@ugg/shared/utils/role-helpers";
import classNames from "classnames";
import { RoleS } from "@ugg/shared/utils/role-helpers";
import { SvgYearInReviewKda } from "@ugitgud/legos/ui/icon/SVG/year-in-review/YearInReviewKda";
import { SvgYearInReviewKills } from "@ugitgud/legos/ui/icon/SVG/year-in-review/YearInReviewKills";
import { SvgYearInReviewDeath } from "@ugitgud/legos/ui/icon/SVG/year-in-review/YearInReviewDeath";
import { SvgYearInReviewAssists } from "@ugitgud/legos/ui/icon/SVG/year-in-review/YearInReviewAssists";
import { SvgYearInReviewCs } from "@ugitgud/legos/ui/icon/SVG/year-in-review/YearInReviewCs";
import { SvgYearInReviewGold } from "@ugitgud/legos/ui/icon/SVG/year-in-review/YearInReviewGold";
import { SvgYearInReviewWards } from "@ugitgud/legos/ui/icon/SVG/year-in-review/YearInReviewWards";
import { SvgYearInReviewTower } from "@ugitgud/legos/ui/icon/SVG/year-in-review/YearInReviewTower";
import { SvgYearInReviewDragon } from "@ugitgud/legos/ui/icon/SVG/year-in-review/YearInReviewDragon";
import { SvgYearInReviewBaron } from "@ugitgud/legos/ui/icon/SVG/year-in-review/YearInReviewBaron";
import { useState } from "react";

export interface StatCardData {
  games: number;
  wins: number;
  timePlayed: number;
  kda: number;
  avgKills: number;
  avgDeaths: number;
  avgAssists: number;
  avgCreepScore: number;
  avgGold: number;
  avgWards: number;
  avgTowers: number;
  avgDragons: number;
  avgBarons: number;
  fastestTower: number;
  fastestDragon: number;
}
export interface StatCardModuleProps {
  srStats?: StatCardData;
  aramStats?: StatCardData;
}

const TopStatCard = ({ value, category }: { value: number; category: string }) => {
  return (
    <div className="flex flex-col items-center pt-[6px] pb-[12px] rounded-[3px] gap-y-[3px] min-w-[111px] min-h-[81px] shrink-0 [background:var(--gradient01,linear-gradient(227deg,#313160_-134.31%,#191937_100%))] shadow-[0_1px_0_0_#575792_inset,0_2px_4px_0_rgba(0,0,0,0.25)]">
      <div className="text-white text-[36px] font-medium leading-[1.2] font-[Barlow]">{value}</div>
      <div className="text-[#CDDCFE] text-[14px] font-medium leading-[1.2] font-[Barlow]">{category}</div>
    </div>
  );
};

const GameModeCard = ({
  icon,
  gameMode,
  active,
  onClick,
}: {
  icon: ReactNode;
  gameMode: string;
  active: boolean;
  onClick: () => void;
}) => {
  return (
    <div
      onClick={onClick}
      className={classNames(
        "flex items-center justify-center gap-x-[8px] w-full p-[12px] rounded-[3px] transition-colors duration-200 cursor-pointer",
        {
          "bg-[#383864]": active, // Active state background (lighter purple/blue)
          "bg-[#25254B] hover:bg-[#2e2e5c]": !active, // Inactive state
        },
      )}
    >
      {icon}
      <div className={classNames("text-[13px] font-bold", active ? "text-white" : "text-[#8080B2]")}>{gameMode}</div>
    </div>
  );
};

const StatCard = ({ icon, text, value }: { icon: ReactNode; text: string; value: string | number }) => {
  return (
    <div className="flex justify-between border-b-[1px] border-[#11112A] w-full py-[13px]">
      <div className="flex justify-start gap-x-[8px]">
        <Icon icon={icon} className="text-[#383864] w-[24px] h-[24px]" />
        <div className="text-[#CDDCFE] text-[18px] font-[Barlow] font-medium">{text}</div>
      </div>
      <div className="text-white text-[18px] font-[Barlow] font-bold italic">{value}</div>
    </div>
  );
};

export const StatCardModule: React.FC<StatCardModuleProps> = ({ srStats, aramStats }) => {
  const hasSrStats = srStats && srStats.games > 10;
  const hasAramStats = aramStats && aramStats.games > 10;

  const [activeMode, setActiveMode] = useState<"SR" | "ARAM">(hasSrStats ? "SR" : "ARAM");
  const activeStats = activeMode === "SR" ? srStats : aramStats;

  if (!activeStats) return null;

  const days = (activeStats.timePlayed / (24 * 60 * 60)).toFixed(1);
  const hours = Math.floor(activeStats.timePlayed / 3600);

  const combatStatsLeft = (
    <>
      <StatCard icon={<SvgYearInReviewKda />} text={"KDA"} value={activeStats.kda.toFixed(2)} />
      <StatCard icon={<SvgYearInReviewKills />} text={"Avg. Kills"} value={activeStats.avgKills} />
      <StatCard icon={<SvgYearInReviewDeath />} text={"Avg. Deaths"} value={activeStats.avgDeaths} />
    </>
  );

  const combatStatsRight = (
    <>
      <StatCard icon={<SvgYearInReviewAssists />} text={"Avg. Assists"} value={activeStats.avgAssists} />
      <StatCard icon={<SvgYearInReviewCs />} text={"Avg. CS / Min"} value={activeStats.avgCreepScore.toFixed(1)} />
      <StatCard icon={<SvgYearInReviewGold />} text={"Avg. Gold / Min"} value={activeStats.avgGold} />
    </>
  );

  const objectiveStats = (
    <>
      <StatCard icon={<SvgYearInReviewWards />} text={"Avg. Wards / Game"} value={activeStats.avgWards.toFixed(1)} />
      <StatCard icon={<SvgYearInReviewTower />} text={"Avg. Towers"} value={activeStats.avgTowers} />
      <StatCard
        icon={<SvgYearInReviewTower />}
        text={"Fastest Tower"}
        value={activeStats.fastestTower > 0 ? formatTime(activeStats.fastestTower) : "N/A"}
      />
      <StatCard icon={<SvgYearInReviewDragon />} text={"Avg. Dragons"} value={activeStats.avgDragons} />
      <StatCard
        icon={<SvgYearInReviewDragon />}
        text={"Fastest Dragon"}
        value={activeStats.fastestDragon > 0 ? formatTime(activeStats.fastestDragon) : "N/A"}
      />
      <StatCard icon={<SvgYearInReviewBaron />} text={"Avg. Barons"} value={activeStats.avgBarons} />
    </>
  );

  return (
    <div className="flex flex-col items-center w-[498px]">
      <div className="flex gap-x-[18px] mb-[48px]">
        <TopStatCard value={activeStats.games} category={"Games"} />
        <TopStatCard value={activeStats.wins} category={"Wins"} />
        <TopStatCard value={Number(days)} category={"Days"} />
        <TopStatCard value={hours} category={"Hours"} />
      </div>
      {(hasSrStats || hasAramStats) && (
        <div className="flex flex w-full gap-x-3 mb-[34px] ">
          {hasSrStats && (
            <GameModeCard
              icon={getYirSVG(
                RoleS.MIDDLE,
                activeMode === "SR",
                classNames("w-[18px] h-[18px]", {
                  "text-[#F4C874]": activeMode === "SR",
                  "text-[#8080B2]": activeMode !== "SR",
                }),
              )}
              gameMode={"Summoner's Rift"}
              active={activeMode === "SR"}
              onClick={() => setActiveMode("SR")}
            />
          )}

          {hasAramStats && (
            <GameModeCard
              icon={getYirSVG(
                RoleS.ARAM,
                activeMode === "ARAM",
                classNames("w-[18px] h-[18px]", {
                  "text-[#F4C874]": activeMode === "ARAM",
                  "text-[#8080B2]": activeMode !== "ARAM",
                }),
              )}
              gameMode={"ARAM"}
              active={activeMode === "ARAM"}
              onClick={() => setActiveMode("ARAM")}
            />
          )}
        </div>
      )}
      <div className="flex gap-x-[18px] w-full">
        <div className="flex flex-col w-full">
          {combatStatsLeft}
          {activeMode === "SR" && combatStatsRight}
        </div>
        <div className="flex flex-col w-full">{activeMode === "SR" ? objectiveStats : combatStatsRight}</div>
      </div>
    </div>
  );
};

/**** helper functions ---------------------------------------------------------------------------------------------------------------- */

const formatTime = (seconds: number): string => {
  const minutes = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${minutes}:${secs.toString().padStart(2, "0")}`;
};
